import React from "react"
import { graphql } from "gatsby"
import { Link } from "../../../../plugins/gatsby-plugin-atollon"
import StructuredText from "../custom-structured-text-renderer"
import parse from "html-react-parser"
import { Australia } from "../../icons"

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined"

// Small fallback for CSS.escape in older browsers
const cssEscape = (str) => {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(str)
  return String(str)
    .replace(/[^a-zA-Z0-9_\u00A0-\uFFFF-]/g, (ch) => `\\${ch}`)
    .replace(/^\d/, (d) => `\\3${d} `)
}

const LocationList = ({ items, slug }) => {
  const elementRef = React.useRef(null)

  // Scroll-triggered line reveal (GSAP loaded only in browser)
  React.useEffect(() => {
    if (!isBrowser) return
    let stInstance = null

    ;(async () => {
      try {
        const gsapMod = await import("gsap")
        const { ScrollTrigger } = await import("gsap/ScrollTrigger")
        const gsap = gsapMod?.gsap ?? gsapMod.default ?? gsapMod
        gsap.registerPlugin(ScrollTrigger)

        const root = elementRef.current
        if (!root) return

        const lines = root.querySelectorAll(".line")
        if (!lines?.length) return

        stInstance = ScrollTrigger.create({
          trigger: root,
          start: "top bottom",
          onEnter: () => {
            lines.forEach((el) => el.classList.add("is-inview"))
          },
        })
      } catch {
        // Fail gracefully if GSAP isn't available
      }
    })()

    return () => {
      try {
        stInstance && stInstance.kill && stInstance.kill()
      } catch {}
    }
  }, [slug])

  // Hover syncing between list items and map circles
  React.useEffect(() => {
    if (!isBrowser) return

    const root = elementRef.current
    if (!root) return

    const locationItems = root.querySelectorAll(".location-list__item")
    if (!locationItems?.length) return

    const itemHandlers = new WeakMap()
    const circleHandlers = new WeakMap()

    const wirePair = (item, circle) => {
      const onItemEnter = () => {
        item.classList.add("active")
        circle.classList.add("active")
      }
      const onItemLeave = () => {
        item.classList.remove("active")
        circle.classList.remove("active")
      }
      const onCircleEnter = () => {
        item.classList.add("active")
        circle.classList.add("active")
      }
      const onCircleLeave = () => {
        item.classList.remove("active")
        circle.classList.remove("active")
      }

      item.addEventListener("mouseenter", onItemEnter)
      item.addEventListener("mouseleave", onItemLeave)
      circle.addEventListener("mouseenter", onCircleEnter)
      circle.addEventListener("mouseleave", onCircleLeave)

      itemHandlers.set(item, { enter: onItemEnter, leave: onItemLeave })
      circleHandlers.set(circle, { enter: onCircleEnter, leave: onCircleLeave })
    }

    // Wire from items -> circles
    locationItems.forEach((item) => {
      const circleId = item.getAttribute("data-name")
      if (!circleId) return
      const selector = `svg#Australia circle#${cssEscape(circleId)}`
      const circle = document.querySelector(selector)
      if (circle) {
        wirePair(item, circle)
      } else {
        // eslint-disable-next-line no-console
        console.warn(`No circle found with ID: ${circleId}`)
      }
    })

    // Also wire circles -> items
    const circles = document.querySelectorAll("svg#Australia circle")
    circles.forEach((circle) => {
      const circleId = circle.getAttribute("id")
      if (!circleId) return
      const match = Array.from(locationItems).find(
        (item) => item.getAttribute("data-name") === circleId
      )
      if (match && !circleHandlers.get(circle)) {
        wirePair(match, circle)
      }
    })

    return () => {
      locationItems.forEach((item) => {
        const h = itemHandlers.get(item)
        if (h) {
          item.removeEventListener("mouseenter", h.enter)
          item.removeEventListener("mouseleave", h.leave)
        }
      })
      const allCircles = document.querySelectorAll("svg#Australia circle")
      allCircles.forEach((circle) => {
        const h = circleHandlers.get(circle)
        if (h) {
          circle.removeEventListener("mouseenter", h.enter)
          circle.removeEventListener("mouseleave", h.leave)
        }
      })
    }
  }, [items])

  return (
    <section className="location-list" ref={elementRef}>
      <div className="location-list__inner">
        <div className="location-list__left">
          <Australia />
        </div>
        <div className="location-list__right">
          <div className="line line--vertical center-top" />
          <div className="location-list__items">
            {Array.isArray(items) &&
              items.length > 0 &&
              items.map((el, i) => {
                const dataName = (el?.title ?? "")
                  .toString()
                  .toLowerCase()
                  .trim()
                  .replace(/\s+/g, "-")
                return (
                  <div key={i} className="location-list__item" data-name={dataName}>
                    {el?.title ? <h2>{parse(el.title)}</h2> : null}
                    {el?.richContent ? (
                      <div className="content">
                        <StructuredText data={el.richContent} />
                      </div>
                    ) : null}
                    {el?.mail ? <Link to={`mailto:${el.mail}`}>{el.mail}</Link> : null}
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default LocationList

export const query = graphql`
  fragment LocationListProps on DatoCmsLocationList {
    ... on DatoCmsLocationList {
      id
      items {
        title
        richContent {
          value
        }
        mail
      }
    }
  }
`
